/**
 * TodaySectionList — Today's seven day sections and every row inside them.
 *
 * Lifted verbatim out of TodayView, which the Stage 2b spec forbids growing:
 * "Lift the section loop, the drag wiring and the collapse state into their own
 * units first. If this file is longer at the end than it started, the work was
 * done wrong."
 *
 * Reads `useScheduleActionsContext()` directly rather than taking forty
 * handler props — it is the same context TodayView reads, and threading it
 * through would be a second copy of the same wiring.
 */
import { useCallback, useMemo, useState } from 'react'
import type { Task } from '@/types/task'
import type { TimelineItem } from '@/types/timeline'
import type { DaySection } from '@/lib/timeUtils'
import type { ParserContext } from '@/lib/quickInputParser'
import type { useTimelineInsert } from '@/hooks/useTimelineInsert'

import { parseRoutineTimelineId } from '@/lib/today/doseExpansion'
import { sectionKey } from '@/lib/today/sectionCollapse'
import { useScheduleActionsContext } from '@/contexts/ScheduleActionsContext'
import { computeAnchorTime } from '@/lib/timelineAnchor'

import { DaySectionHeader } from '@/components/schedule/DaySectionHeader'
import { TimelineInsertPoint } from './TimelineInsertPoint'
import { ScheduleItem } from './ScheduleItem'
import { RoutineCollectionRow } from './RoutineCollectionRow'
import { useHideForToday } from '@/components/routine/useHideForToday'
import { ShareToFamilyNudge } from './ShareToFamilyNudge'
import { TodayBandDropZone, TodayGapDropZone } from './TodayDropZones'
import { TodayDraggableRow } from './TodayDraggableRow'
import { GroupNameInput } from './GroupNameInput'
import { useTodayDragState } from './TodayDragProvider'
import { refusalFor } from '@/lib/today/todayDrop'
import { DEFAULT_SECTION_CAP } from '@/lib/today/pageCap'
import { curateUnits } from '@/lib/today/curate'
import { computeOpenSpans, computeSpine } from '@/lib/today/openSpace'
import { effectiveStartTime } from '@/lib/timeUtils'
import { OpenSpaceLine } from './OpenSpaceLine'
import { TimelineSpine } from './TimelineSpine'
import { countRoutineRowUnits } from '@/lib/today/routineCollections'
import { ROW_SHELL, ROW_GRID, LANE, MARK } from './todayRowGrid'
import type { RowMark } from './todayPreview'

/** A pending change card's mark, drawn around a row (Week's dashed look). */
function previewClass(mark: RowMark | undefined): string {
  if (!mark) return ''
  return `today-preview-row is-${mark.tone}${mark.struck ? ' is-struck' : ''}`
}
function PreviewNote({ mark }: { mark: RowMark | undefined }) {
  if (!mark) return null
  return (
    <>
      {mark.note && <span className="wc-proposed-note today-preview-note">{mark.note}</span>}
      <span className="sr-only"> (change proposed, not saved yet)</span>
    </>
  )
}

// ─── Meal detection ────────────────────────────────────────────────────────────

const MEAL_RE = /breakfast|brunch|lunch|dinner|supper/i

function isMealItem(id: string, type: string, title: string): boolean {
  return String(id).startsWith('meal:') || (type === 'event' && MEAL_RE.test(title))
}

/**
 * The "Up next" marker — replaces the UpNextHero card (2026-08-18). The next
 * commitment stays IN the timeline where the eye expects it; this one small
 * line plus a tinted row is the entire treatment. Lifting it into a hero left
 * its home section rendering an empty heading labelled "· up next".
 */
function UpNextMarker({ status }: { status?: string }) {
  return (
    <div className="flex items-center gap-2 px-3 md:px-0 pt-1.5 pb-1" data-testid="up-next-marker">
      <span className="text-[11px] uppercase tracking-wider font-bold text-accent-600">Up next</span>
      {status && <span className="text-[12px] text-neutral-500">· {status}</span>}
    </div>
  )
}

/** Row tint for the up-next commitment — subtle, no layout shift. On desktop
 *  the time sits on the column's edge (layout system), so the marker moves
 *  out into the page gutter instead of drawing over the time. */
const UP_NEXT_ROW_CLASS = 'relative rounded-r-md border-l-2 border-primary-600 bg-primary-50/40 md:rounded-none md:border-l-0 md:bg-transparent md:before:absolute md:before:-left-[17px] md:before:inset-y-1 md:before:w-0.5 md:before:rounded-full md:before:bg-primary-600'

/** Locate a rendered timeline item by id across every section. */
export function findTimelineItem(
  grouped: Record<DaySection, TimelineItem[]>,
  id: string,
): TimelineItem | null {
  for (const list of Object.values(grouped)) {
    const found = list.find((i) => i.id === id)
    if (found) return found
  }
  return null
}

/** The dish as the row says it: "Dinner: Tofu + Edamame Soba Noodle Bowl".
 *  A synthesized meal event may lead with an emoji; the row's type is what
 *  the words say, so the glyph goes. */
export function mealRowTitle(raw: string): string {
  return raw.replace(/^[^\p{L}\p{N}(["']+/u, '').trim()
}

export interface TodaySectionListProps {
  sectionsOrder: DaySection[]
  grouped: Record<DaySection, TimelineItem[]>
  viewedDate: Date
  isMobile: boolean
  selectedItemId: string | null
  /** The next commitment, highlighted IN PLACE — never lifted out of the list. */
  upNextId: string | undefined
  /** e.g. "starts in ~2.5 hr" — rendered on the up-next marker line. */
  upNextStatus?: string
  /** Wall clock used to measure open space. Injected so tests are deterministic. */
  now?: Date
  firstSectionItemId: string | null
  collapsedKeys: Set<string>
  openedByUser: Set<string>
  onToggleSection: (section: DaySection, currentlyCollapsed: boolean) => void
  /** A pending change card's mark on a row (todayPreview.ts): dashed teal
   *  (proposed) or grey (leaving), struck for done/skipped/leaving. */
  previewOf?: (item: TimelineItem) => RowMark | undefined
  tasksMap: Map<string, Task>
  shareNudgeByEventId: Map<string, { eventId: string; context: string }>
  parserContext: ParserContext
  insert: ReturnType<typeof useTimelineInsert>
  onSelectItem: (id: string | null) => void
  onToggleTask: (taskId: string) => void
  onCompleteRoutine?: (routineId: string, completed: boolean, completedAt?: Date) => void
  onCompleteEvent?: (eventId: string, completed: boolean) => void
  panelOpen?: boolean
  onClosePanel?: () => void
  /** True when this event sits on a read-only calendar — it refuses the drag. */
  isReadOnlyEvent: (item: TimelineItem) => boolean
  /** Raw task id of a just-created group, rendered as an inline name field. */
  renamingGroupId?: string | null
  onRenameGroupDone?: () => void
  /** The signed-in member — rows show initials only for someone ELSE's work. */
  currentMemberId?: string | null
  /**
   * Today's journal renders the day in slices (My focus / Still ahead /
   * Earlier today), each through this same list. Only ONE slice may own a
   * section's drop targets (band and gap ids are per section); a slice that
   * doesn't passes false and renders plain rows — still draggable, never a
   * target, no empty bands while a drag is live, no insert points.
   */
  dropTargets?: boolean
  /** Rows of a section rendered by an EARLIER slice. Gap indices count from
   *  the full section so the resolver (todayDrop.ts) retimes against it. */
  gapOffset?: Partial<Record<DaySection, number>>
  /** The unscheduled section's "Anytime · M of N done" fold. My focus lists
   *  chosen untimed work plainly, so it turns this off. Default true. */
  anytimeHeader?: boolean
}

/** No drop zone: the slice renders its rows plainly. */
function Plain({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

export function TodaySectionList({
  sectionsOrder,
  grouped,
  viewedDate,
  isMobile,
  selectedItemId,
  upNextId,
  upNextStatus,
  now,
  firstSectionItemId,
  collapsedKeys,
  openedByUser,
  onToggleSection,
  previewOf,
  tasksMap,
  shareNudgeByEventId,
  parserContext,
  insert,
  onSelectItem,
  onToggleTask,
  onCompleteRoutine,
  onCompleteEvent,
  panelOpen,
  onClosePanel,
  isReadOnlyEvent,
  renamingGroupId,
  onRenameGroupDone,
  currentMemberId = null,
  dropTargets = true,
  gapOffset,
  anytimeHeader = true,
}: TodaySectionListProps) {
  const Band = dropTargets ? TodayBandDropZone : Plain
  const Gap = dropTargets ? TodayGapDropZone : Plain
  const ctx = useScheduleActionsContext()
  const { dragging } = useTodayDragState()
  // "Hide for today" skips this day's occurrence only (a 'skipped' instance),
  // with Undo in the canvas strip — never Rest (visibility/paused_until).
  const { hideForToday } = useHideForToday()

  // Which sections the user has expanded past the cap. Not persisted: a cap is
  // about this reading of the page, not a standing preference.
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set())
  const expandSection = useCallback((key: string) => {
    setExpandedSections((prev) => new Set(prev).add(key))
  }, [])
  const {
    onToggleWaiting, onUpdateTask,
    onAssignTask, onAssignTaskAll, onAssignEvent, onAssignEventAll,
    onAssignRoutine, onAssignRoutineAll,
    onSkipRoutine, onUpdateRoutine, onRegisterUndo,
    onSkipEvent, onUpdateEventContext,
    onOpenTask,
    contactsMap, projectsMap, familyMembers = [],
    eventNotesMap,
  } = ctx

  const onCreateTaskAt = ctx.onCreateTaskAt
  const onCreateEventAt = ctx.onCreateEventAt
  const onCreateRoutineAt = ctx.onCreateRoutineAt

  // Core members are the default diners until per-meal diner assignment
  // lands; the row says how many, the meal's details say who.
  const servesCount = useMemo(() => {
    const core = familyMembers.filter((m) => m.member_type === 'core').length
    return core > 0 ? core : undefined
  }, [familyMembers])

  // Open space is a property of the whole day, not of one band: the gap that
  // matters most on a light day runs from a morning routine clear through to
  // dinner. Computed over every timed item in render order across all
  // sections, INCLUDING any the section cap will hide — a hidden commitment
  // still occupies its hour, and a span that ignored it would overstate the
  // room by exactly that much.
  const openSpans = useMemo(
    () => computeOpenSpans(
      sectionsOrder.flatMap((s) => grouped[s] ?? []),
      { now: now ?? new Date(), viewedDate },
    ),
    [sectionsOrder, grouped, now, viewedDate],
  )

  // Same population, same order as the spans above — the spine has to agree
  // with the gaps or the line will run straight through a hole it just said
  // was open.
  const spine = useMemo(
    () => computeSpine(sectionsOrder.flatMap((s) => grouped[s] ?? []), openSpans),
    [sectionsOrder, grouped, openSpans],
  )

  return (
    <>
      {sectionsOrder.map((section) => {
        const allSectionItems = grouped[section]
        const isEmpty = !allSectionItems || allSectionItems.length === 0
        // Empty sections stay hidden for reading, but a drag needs somewhere to
        // aim: you cannot drop something at 6 AM if the Early morning band
        // isn't on screen. Unscheduled is never a drop target, so it stays
        // hidden either way.
        if (isEmpty && (!dragging || !dropTargets || section === 'unscheduled')) return null

        const items = allSectionItems ?? []
        // The cap bounds what RENDERS. Every count below still comes from the
        // full `items` — the header is where the truth about the day lives.
        // Capped by GROUP, not by row: a group renders as one enclosed card
        // whose borders come from adjacency, so cutting the run in half leaves
        // a card with no bottom edge.
        // curateUnits, not capUnits: same unit model and the same honest count,
        // but the survivors are chosen by relevance rather than by position, so
        // the row you needed can't be the one hidden behind "+9 more" purely
        // because of where it landed in the sort.
        const { visible, hiddenCount } = curateUnits(
          items,
          DEFAULT_SECTION_CAP,
          expandedSections.has(sectionKey(section)),
          (item) => !item.isSubtask,
        )
        const completedCount = items.filter((i) => i.completed).length
        const restAllDone = items.length > 0 && completedCount === items.length
        // The untimed-routine slab collapses to one "Anytime · M of N done"
        // row regardless of whether it holds 12 routines or 60 — computed
        // from the same `items` the rows below would render, so it can't
        // drift from what's actually on screen.
        const anytimeSummary = section === 'unscheduled' ? countRoutineRowUnits(items) : undefined
        const key = sectionKey(section)

        // The flat agenda (2026-08-18): timed sections carry NO header and never
        // collapse — the day reads as one time-ordered list, the way a paper
        // plan does. The band structure itself stays, because bands are the
        // drag targets; while a drag is live the headers reappear as labels so
        // there is something to aim at. Only Anytime (unscheduled) keeps a
        // permanent header: its collapsed "Anytime · M of N done" row is the
        // fixed-budget summary of the untimed-routine slab, and folding it is
        // the point. Precedence there: an explicit fold always wins; an
        // explicit open overrides the auto rule; otherwise auto-collapse when
        // everything is done. `collapsedKeys` and `openedByUser` are
        // independent facts — never derive one from the other.
        const collapsed = section === 'unscheduled' && anytimeHeader
          ? collapsedKeys.has(key)
            ? true
            : openedByUser.has(key)
              ? false
              : restAllDone
          : false
        const showHeader = section === 'unscheduled' ? anytimeHeader : (dragging && dropTargets)
        const offset = gapOffset?.[section] ?? 0

        return (
          <section key={section}>
            <Band section={section}>
            {showHeader && (
              <DaySectionHeader
                section={section}
                itemCount={items.length}
                completedCount={completedCount}
                collapsed={collapsed}
                onToggle={() => onToggleSection(section, collapsed)}
                anytimeSummary={anytimeSummary}
              />
            )}
            {!collapsed && (
              <div className="space-y-1 md:space-y-0.5">
                {visible.map((item, itemIndex) => {
                  const isUpNext = !!upNextId && item.id === upNextId
                  const taskId = item.id.startsWith('task-') ? item.id.replace('task-', '') : null
                  const contactName = item.contactId && contactsMap?.get(item.contactId)?.name || undefined
                  const projectName = item.projectId && projectsMap?.get(item.projectId)?.name || undefined
                  const parentTaskId = item.parentTaskId
                  const parentTaskName = parentTaskId ? tasksMap.get(parentTaskId)?.title : undefined
                  const isFirstItem = item.id === firstSectionItemId
                  // No affordance at all when the item refuses — a read-only
                  // event that accepted the drag would fail at Google and
                  // spring back for no visible reason.
                  const dragRefused = !!refusalFor(item, isReadOnlyEvent)

                  // ── Group cards ──────────────────────────────────────
                  // A parent task and its adjacent subtask rows (grouping.ts
                  // places children directly after their parent within a
                  // section) render inside one enclosed, tinted card. Roles
                  // are derived purely from adjacency, so a parent whose
                  // children live in another section gets no card.
                  const prevItem = itemIndex > 0 ? visible[itemIndex - 1] : null
                  const nextItem = itemIndex < visible.length - 1 ? visible[itemIndex + 1] : null
                  const isGroupParent = !!taskId && !!nextItem && !!nextItem.isSubtask && nextItem.parentTaskId === taskId
                  const isGroupChild = !!item.isSubtask && !!prevItem &&
                    (prevItem.id === `task-${item.parentTaskId}` ||
                      (!!prevItem.isSubtask && prevItem.parentTaskId === item.parentTaskId))
                  const isLastGroupChild = isGroupChild &&
                    (!nextItem || !(nextItem.isSubtask && nextItem.parentTaskId === item.parentTaskId))
                  const groupCardClass = isGroupParent
                    ? 'rounded-t-2xl border border-b-0 border-primary-200/70 bg-primary-50/30 pt-0.5'
                    : isLastGroupChild
                      ? 'border-x border-b border-primary-200/70 bg-primary-50/30 rounded-b-2xl pb-1 pl-4'
                      : isGroupChild
                        ? 'border-x border-primary-200/70 bg-primary-50/30 pl-4'
                        : ''
                  // Don't offer an insert point between a parent and its
                  // children — a task added there wouldn't be in the group.
                  const showInsert = !isGroupChild && dropTargets

                  // The free run that ends where this item begins, if any.
                  const openSpan = openSpans.get(item.id)
                  const spineSegments = spine.get(item.id)

                  // Insert point before this item
                  const prevItemForInsert = itemIndex > 0 ? visible[itemIndex - 1] : null
                  const insertCtxBefore = {
                    before: prevItemForInsert?.startTime ?? null,
                    after: item.startTime ?? null,
                    section,
                    date: viewedDate,
                  }
                  const insertBefore = (
                    <Gap section={section} index={itemIndex + offset}>
                    <TimelineInsertPoint
                      onPick={(k) => insert.handlePick(insertCtxBefore, k)}
                      onCreate={(kind, r) => {
                        if (kind === 'task') onCreateTaskAt?.(r)
                        else if (kind === 'event') onCreateEventAt?.(r)
                        else onCreateRoutineAt?.(r)
                      }}
                      quickInput={{
                        anchorTime: computeAnchorTime(insertCtxBefore),
                        parserContext,
                      }}
                    />
                    </Gap>
                  )

                  // Evening meal gets a special card (desktop only — on mobile
                  // we let meal items render as compact rows to match the
                  // pre-redesign list). 'night' too: evening now ends at 20:59,
                  // so a 21:00 dinner is a night item and would otherwise lose
                  // its meal-card treatment.
                  if (
                    !isMobile &&
                    (section === 'evening' || section === 'night') &&
                    isMealItem(item.id, item.type, item.title)
                  ) {
                    // effectiveStartTime, not startTime: an all-day "Dinner:
                    // ..." event lands in this branch via the same meal
                    // inference that filed it under evening, but its stored
                    // start is the all-day instant — printing that verbatim
                    // is what made the evening card announce "Dinner at
                    // 8:00 AM".
                    const mealStart = effectiveStartTime(item)
                    const timeLabel = mealStart
                      ? mealStart.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : ''
                    const fromPlan = String(item.id).startsWith('meal:')
                    return (
                      <div key={item.id}>
                        {openSpan && <OpenSpaceLine span={openSpan} />}
                        {showInsert && insertBefore}
                        <TodayDraggableRow itemId={item.id} disabled={dragRefused}>
                        <div {...(isFirstItem ? { 'data-today-first': '' } : {})}>
                          {/* Dinner is a row like every other row (Today,
                              2026-09-21): time in the gutter, a circle, the
                              dish, one muted line. The peach card, the
                              serif, the diner avatars and the recipe button
                              made it the largest object on the page; who is
                              eating and the recipe are in its details. */}
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelectItem(item.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectItem(item.id) } }}
                            className={`group cursor-pointer ${ROW_SHELL} border-transparent transition-all duration-200 hover:bg-primary-50/50 hover:border-primary-100 ${selectedItemId === item.id ? 'bg-primary-50 border-primary-200 shadow-md ring-1 ring-primary-200' : ''}`}
                          >
                            <div className={ROW_GRID}>
                              <TimelineSpine above={spineSegments?.above} below={spineSegments?.below} />
                              <div className={`${LANE} text-xs font-medium tabular-nums text-neutral-500`}>
                                {timeLabel}
                              </div>
                              <div className={`${MARK} flex items-center justify-center relative z-[1]`}>
                                <span
                                  aria-hidden
                                  className="w-5 h-5 rounded-full border-2 border-neutral-300 bg-bg-base"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[16px] leading-snug font-medium text-neutral-800 line-clamp-2 break-words">
                                  {mealRowTitle(item.title)}
                                </div>
                                {(servesCount != null || fromPlan) && (
                                  <div className="text-[12px] text-neutral-500 leading-tight mt-0.5">
                                    {[servesCount != null ? `Serves ${servesCount}` : null, fromPlan ? 'Meal plan' : null].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        </TodayDraggableRow>
                      </div>
                    )
                  }

                  // Routine collection — collapsed row with per-step completion
                  if (item.type === 'routine-collection') {
                    return (
                      <div key={item.id} data-item-id={item.id}>
                        {openSpan && <OpenSpaceLine span={openSpan} />}
                        {showInsert && insertBefore}
                        {isUpNext && <UpNextMarker status={upNextStatus} />}
                        <TodayDraggableRow itemId={item.id} disabled={dragRefused}>
                        <div className={[isUpNext ? UP_NEXT_ROW_CLASS : '', previewClass(previewOf?.(item))].filter(Boolean).join(' ') || undefined}>
                        <RoutineCollectionRow
                          item={item}
                          familyMembers={familyMembers}
                          onAssignAll={onAssignRoutineAll ? (memberIds) => onAssignRoutineAll(item.id.replace('routine-collection-', ''), memberIds) : undefined}
                          onSelect={() => onSelectItem(item.id)}
                          onSelectStep={(stepId) => onSelectItem(stepId)}
                          onCompleteStep={(stepTimelineId, completed) => {
                            if (!onCompleteRoutine) return
                            const { routineId, slot } = parseRoutineTimelineId(stepTimelineId)
                            const entityId = slot === null ? routineId : `${routineId}#${slot}`
                            onCompleteRoutine(entityId, completed)
                          }}
                          onSkipStep={onSkipRoutine ? (stepTimelineId) => {
                            const { routineId, slot } = parseRoutineTimelineId(stepTimelineId)
                            const entityId = slot === null ? routineId : `${routineId}#${slot}`
                            onSkipRoutine(entityId)
                          } : undefined}
                          onCompleteStepAt={onCompleteRoutine ? (stepTimelineId, completedAt) => {
                            const { routineId, slot } = parseRoutineTimelineId(stepTimelineId)
                            const entityId = slot === null ? routineId : `${routineId}#${slot}`
                            onCompleteRoutine(entityId, true, completedAt)
                          } : undefined}
                          // Offered where routines are editable (the same gate
                          // as before), but the write is the instance writer's.
                          onHideToday={onUpdateRoutine ? () => {
                            // Skip the viewed day's occurrence of the whole
                            // collection. The routine keeps running: back on
                            // its next day, still on the week and the kiosk.
                            const parentId = item.id.replace('routine-collection-', '')
                            void hideForToday(parentId, item.title, viewedDate)
                          } : undefined}
                          // "Remove from Today" now does what it says: the
                          // routine keeps running (and keeps its place on the
                          // kitchen wall), it just stops taking a row here.
                          // It used to write visibility:'reference', which is
                          // Resting — it stopped the routine everywhere, which
                          // is a bigger promise than the label makes (Scott,
                          // 2026-09-07). Stopping a routine outright is the
                          // Active switch in its panel.
                          onRemove={onUpdateRoutine ? () => {
                            const parentId = item.id.replace('routine-collection-', '')
                            onUpdateRoutine(parentId, { show_on_timeline: false })
                            onRegisterUndo?.(`"${item.title}" is off Today`, () => {
                              onUpdateRoutine(parentId, { show_on_timeline: true })
                            })
                          } : undefined}
                        />
                        <PreviewNote mark={previewOf?.(item)} />
                        </div>
                        </TodayDraggableRow>
                      </div>
                    )
                  }

                  // A group the drag just created: its header row is an open
                  // name field until it is committed or dismissed. Not
                  // draggable while editing — the pointer belongs to the text.
                  if (taskId && taskId === renamingGroupId) {
                    return (
                      <div key={item.id}>
                        {openSpan && <OpenSpaceLine span={openSpan} />}
                        {showInsert && insertBefore}
                        <div data-item-id={item.id} className={groupCardClass || undefined}>
                          <GroupNameInput
                            initialName={item.title}
                            onCommit={(name) => {
                              if (name !== item.title) onUpdateTask?.(taskId, { title: name })
                              onRenameGroupDone?.()
                            }}
                            onCancel={() => onRenameGroupDone?.()}
                          />
                        </div>
                      </div>
                    )
                  }

                  // Standard schedule item
                  return (
                    <div key={item.id} className={isGroupChild ? '-mt-1' : undefined}>
                    {openSpan && <OpenSpaceLine span={openSpan} />}
                    {showInsert && insertBefore}
                    {isUpNext && <UpNextMarker status={upNextStatus} />}
                    <TodayDraggableRow itemId={item.id} disabled={dragRefused}>
                    {/* Group chrome wins over the up-next tint: stripping a
                        parent's card top to tint it leaves the group's border
                        broken. The marker line above still says "Up next". */}
                    <div data-item-id={item.id} className={[groupCardClass || (isUpNext ? UP_NEXT_ROW_CLASS : ''), previewClass(previewOf?.(item))].filter(Boolean).join(' ') || undefined} {...(isFirstItem ? { 'data-today-first': '' } : {})}>
                    {(() => {
                      const { routineId: bareRoutineId, slot } = item.type === 'routine'
                        ? parseRoutineTimelineId(item.id)
                        : { routineId: '', slot: null }
                      const routineEntityId = slot === null ? bareRoutineId : `${bareRoutineId}#${slot}`
                      return (
                        <>
                    <ScheduleItem
                      spineAbove={spineSegments?.above}
                      spineBelow={spineSegments?.below}
                      // On a phone dinner is an ordinary row too; it reads
                      // the dish, not the glyph the meal event was given.
                      item={isMobile && isMealItem(item.id, item.type, item.title) ? { ...item, title: mealRowTitle(item.title) } : item}
                      selected={selectedItemId === item.id}
                      onSelect={() => onSelectItem(item.id)}
                      onToggleWaiting={
                        item.type === 'task' && taskId && onToggleWaiting
                          ? () => onToggleWaiting(taskId)
                          : undefined
                      }
                      onToggleComplete={() => {
                        if (item.type === 'task' && taskId) {
                          onToggleTask(taskId)
                        } else if (item.type === 'routine' && onCompleteRoutine) {
                          onCompleteRoutine(routineEntityId, !item.completed)
                        } else if (item.type === 'event' && onCompleteEvent) {
                          onCompleteEvent(item.id.replace('event-', ''), !item.completed)
                        }
                      }}
                      // Per-person items under the row. A subtask IS a task, so
                      // the same handler the parent uses completes it.
                      onToggleSubtask={onToggleTask}
                      // No row reschedule button, no bulk checkbox ("Today,
                      // trimmed", 2026-10-14): a day and time are set in the
                      // item panel, or by dragging onto the day.
                      onSkip={
                        item.type === 'routine' && onSkipRoutine
                          ? () => onSkipRoutine(routineEntityId)
                          : item.type === 'event' && onSkipEvent
                          ? () => onSkipEvent(item.id.replace('event-', ''))
                          : undefined
                      }
                      contactName={contactName}
                      projectName={projectName}
                      projectId={item.projectId ?? undefined}
                      // Inside a group card the parent's name is already the
                      // header two rows up — repeating it on every child is
                      // noise. Keep it for an orphan child, where it is the
                      // only thing saying what this row belongs to.
                      parentTaskName={isGroupChild ? undefined : parentTaskName}
                      parentTaskId={parentTaskId}
                      onOpenParentTask={onOpenTask}
                      familyMembers={familyMembers}
                      assignedTo={item.assignedTo}
                      onAssign={
                        item.type === 'task' && taskId && onAssignTask
                          ? (memberId) => onAssignTask(taskId, memberId)
                          : item.type === 'event' && onAssignEvent
                          ? (memberId) => onAssignEvent(item.id.replace('event-', ''), memberId)
                          : item.type === 'routine' && onAssignRoutine
                          ? (memberId) => onAssignRoutine(bareRoutineId, memberId)
                          : undefined
                      }
                      assignedToAll={
                        item.type === 'event' && eventNotesMap
                          ? eventNotesMap.get(item.id.replace('event-', ''))?.assignedToAll ?? []
                          : item.type === 'task'
                          ? item.originalTask?.assignedToAll ?? []
                          : item.type === 'routine'
                          ? item.originalRoutine?.assigned_to_all ?? []
                          : []
                      }
                      onAssignAll={
                        item.type === 'task' && taskId && onAssignTaskAll
                          ? (memberIds) => onAssignTaskAll(taskId, memberIds)
                          : item.type === 'event' && onAssignEventAll
                          ? (memberIds) => onAssignEventAll(item.id.replace('event-', ''), memberIds)
                          : item.type === 'routine' && onAssignRoutineAll
                          ? (memberIds) => onAssignRoutineAll(bareRoutineId, memberIds)
                          : undefined
                      }
                      onContextChange={
                        item.type === 'task' && taskId && onUpdateTask
                          ? (context) => onUpdateTask(taskId, { context })
                          : item.type === 'routine' && onUpdateRoutine
                          ? (context) => onUpdateRoutine(bareRoutineId, { context })
                          : item.type === 'event' && onUpdateEventContext
                          ? (context) => onUpdateEventContext(item.id.replace('event-', ''), context ?? null)
                          : undefined
                      }
                      panelOpen={panelOpen}
                      onClosePanel={onClosePanel}
                      variant={item.type === 'routine' ? 'minimal' : 'full'}
                      currentMemberId={currentMemberId}
                    />
                    <PreviewNote mark={previewOf?.(item)} />
                    {item.type === 'event' && (() => {
                      const nudge = shareNudgeByEventId.get(item.id.replace('event-', ''))
                      if (!nudge) return null
                      return (
                        <ShareToFamilyNudge
                          contextLabel={nudge.context}
                          onAdd={() => ctx.onShareEventWithFamily?.(nudge.eventId)}
                          onDismiss={() => ctx.onDismissShareNudge?.(nudge.eventId)}
                        />
                      )
                    })()}
                        </>
                      )
                    })()}
                    </div>
                    </TodayDraggableRow>
                    </div>
                  )
                })}
                {/* Trailing insert point: after the last item per section */}
                {dropTargets && (() => {
                  const insertCtxTrailing = {
                    before: visible.length > 0 ? (visible[visible.length - 1].startTime ?? null) : null,
                    after: null,
                    section,
                    date: viewedDate,
                  }
                  return (
                    <Gap section={section} index={visible.length + offset}>
                    <TimelineInsertPoint
                      onPick={(k) => insert.handlePick(insertCtxTrailing, k)}
                      onCreate={(kind, r) => {
                        if (kind === 'task') onCreateTaskAt?.(r)
                        else if (kind === 'event') onCreateEventAt?.(r)
                        else onCreateRoutineAt?.(r)
                      }}
                      quickInput={{
                        anchorTime: computeAnchorTime(insertCtxTrailing),
                        parserContext,
                      }}
                    />
                    </Gap>
                  )
                })()}
                {/* A cap that hides its own truncation is worse than a long
                    page — the count is always stated, and always expandable. */}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => expandSection(sectionKey(section))}
                    className="w-full text-left px-3 md:px-0 py-1.5 text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors"
                  >
                    +{hiddenCount} more today
                  </button>
                )}
              </div>
            )}
            </Band>
          </section>
        )
      })}
    </>
  )
}
